import React, { ReactElement } from 'react';
import Comment from '@/components/Comment';
import {
  useDeleteCommentMutation,
  useGetReviewerCommentsQuery,
  useUpdateCommentStatusMutation,
} from '@/modules/services';
import { Alert, Col, Divider, Modal, Spinner } from 'elmo-elements';
import CommentsType from '@/modules/types/comments';
import { useDispatch, useSelector } from 'react-redux';
import {
  ElearningCommentsStateType,
  actions as elearningCommentsActions,
} from '@/modules/reducers/comments';
import { useParams } from 'react-router-dom';
import { ElearningParamType } from '@/pages/Module/routes';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';

export const ReviewerComments = (): ReactElement => {
  const { elearningId } = useParams<ElearningParamType>();

  const dispatch = useDispatch();
  const elearningCommentsState: ElearningCommentsStateType = useSelector(
    (state: { elearningComments: ElearningCommentsStateType }) =>
      state.elearningComments,
  );

  const [deleteComment] = useDeleteCommentMutation();
  const [updateCommentStatus] = useUpdateCommentStatusMutation();

  const { data: commentsData, isLoading } = useGetReviewerCommentsQuery(
    elearningCommentsState?.reviewerId || '',
    {
      skip: !elearningCommentsState?.reviewerId,
    },
  );

  if (!elearningCommentsState) {
    return <div />;
  }

  const getElearningComments = (comments: CommentsType[]) =>
    comments?.filter((comment) => comment.frame?.elearning.id === elearningId);

  const closeModal = () => {
    dispatch({
      type: elearningCommentsActions.closeCommentsModal.type,
    });
  };

  const displayComments = (comments: CommentsType[]) =>
    comments.length > 0 ? (
      comments.map((comment, index) => (
        <Col key={index} role="listitem">
          <Comment
            title={comment.frame?.title || ''}
            comment={comment}
            onDelete={(id) => {
              deleteComment(id);
            }}
            onUpdate={async (status) => {
              try {
                await updateCommentStatus({
                  id: comment.id,
                  content: {
                    status,
                  },
                }).unwrap();
              } catch (error) {
                dispatch({
                  type: flashMessageActions.addFlashMessage,
                  payload: {
                    isActive: true,
                    message: 'Something went wrong',
                  },
                });
              }
            }}
          />
          <Divider />
        </Col>
      ))
    ) : (
      <Alert message="No comment found!" type="warning" isCloseable={false} />
    );

  return (
    <Modal
      id="ModalDialog"
      type="x-large"
      isOpened={elearningCommentsState.isOpen}
      closeModal={() => closeModal()}
      title={
        commentsData?.[0]?.reviewer.reviewerMetadata.firstName &&
        commentsData?.[0]?.reviewer.reviewerMetadata.lastName
          ? `Comments - ${commentsData?.[0].reviewer.reviewerMetadata.firstName} ${commentsData?.[0].reviewer.reviewerMetadata.lastName}`
          : 'Comments - reviewer'
      }
      closeLabel="Close"
    >
      {isLoading ? (
        <Spinner size="xl" ariaLabel="Loading comment data" />
      ) : (
        displayComments(getElearningComments(commentsData || []))
      )}
    </Modal>
  );
};

export default ReviewerComments;
